// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/Content/BlogForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk, faTimes } from '@fortawesome/free-solid-svg-icons';
import debounce from 'lodash/debounce';
import {
  insertContentBlog,
  updateContentBlog,
  uploadImageContentBlog,
  insertImageContentBlog,
  updateImageContentBlog,
  fetchImageContentBlog,
  deleteImageContentBlog,
} from '../../utils/ExploreBlogHandlers';
import ImagesUploadSection from './IdeasLab/ImagesUploadSection';
import EditorField from './IdeasLab/EditorField';
import { API_ENDPOINTS } from '../../api/api';

import '../../css/IdeasLabForm.scss';

const BlogForm = ({ ideaData, mode, onCancel, onSave }) => {
  const currentDateTime = new Date();
  const defaultDate = currentDateTime.toISOString().split('T')[0];
  const defaultTime = currentDateTime.toTimeString().split(' ')[0];

  const [formData, setFormData] = useState({
    Home_ID: ideaData?.Home_ID || '',
    Member_ID: ideaData?.Member_ID || 1,
    Home_Date: ideaData?.Home_Date || defaultDate,
    Home_Time: ideaData?.Home_Time || defaultTime,
    Home_Visitors: ideaData?.Home_Visitors || 0,
    Home_Sub: ideaData?.Home_Sub || '',
    Home_Short: ideaData?.Home_Short || '',
    Home_Short_img: ideaData?.Home_Short_img || '',
    Home_Short_img_original: ideaData?.Home_Short_img_original || '',
    Home_Detail: ideaData?.Home_Detail || '',
    Home_Detail_img: ideaData?.Home_Detail_img || '',
    Home_Detail_img_original: ideaData?.Home_Detail_img_original || '',
    Home_Category: ideaData?.Home_Category ?? '',
  });

  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [tempFiles, setTempFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [editorValues, setEditorValues] = useState({
    Home_Short: ideaData?.Home_Short || '',
    Home_Detail: ideaData?.Home_Detail || '',
  });
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoryError, setCategoryError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      setCategoryError(null);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(API_ENDPOINTS.SERVICES_VIEW_TYPE, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch categories: ${response.status}`);
        }
        const data = await response.json();
        if (Array.isArray(data)) {
          setCategories(data);
        } else {
          throw new Error('Invalid category data format');
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategoryError(`Failed to load categories: ${error.message}`);
      } finally {
        setIsLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (ideaData) {
      setFormData((prev) => ({
        ...prev,
        Home_ID: ideaData.Home_ID || '',
        Member_ID: ideaData.Member_ID || 1,
        Home_Date: ideaData.Home_Date || defaultDate,
        Home_Time: ideaData.Home_Time || defaultTime,
        Home_Visitors: ideaData.Home_Visitors || 0,
        Home_Sub: mode === 'edit' && prev.Home_Sub !== '' ? prev.Home_Sub : ideaData?.Home_Sub || '',
        Home_Short: mode === 'edit' && prev.Home_Short !== '' ? prev.Home_Short : ideaData?.Home_Short || '',
        Home_Short_img: ideaData.Home_Short_img || '',
        Home_Short_img_original: ideaData.Home_Short_img_original || '',
        Home_Detail: mode === 'edit' && prev.Home_Detail !== '' ? prev.Home_Detail : ideaData?.Home_Detail || '',
        Home_Detail_img: ideaData.Home_Detail_img || '',
        Home_Detail_img_original: ideaData.Home_Detail_img_original || '',
        Home_Category: mode === 'edit' && prev.Home_Category !== '' ? prev.Home_Category : ideaData?.Home_Category ?? '',
      }));
      setEditorValues({
        Home_Short: ideaData?.Home_Short || '',
        Home_Detail: ideaData?.Home_Detail || '',
      });
    }
  }, [ideaData, defaultDate, defaultTime, mode]);

  useEffect(() => {
    const loadImages = async () => {
      if (mode === 'edit' && ideaData?.Home_ID) {
        try {
          setIsLoadingImages(true);
          const images = await fetchImageContentBlog(ideaData.Home_ID);
          console.log('Fetched images:', images);
          if (Array.isArray(images) && images.length > 0) {
            setExistingImages(images);
            setFormData((prev) => ({
              ...prev,
              Home_Short_img: images[0]?.ImgHome_Img || prev.Home_Short_img,
              Home_Short_img_original: images[0]?.ImgHome_ImgOriginal || prev.Home_Short_img_original,
              Home_Detail_img: images[1]?.ImgHome_Img || prev.Home_Detail_img,
              Home_Detail_img_original: images[1]?.ImgHome_ImgOriginal || prev.Home_Detail_img_original,
            }));
          } else {
            setExistingImages([]);
          }
        } catch (err) {
          console.error('Failed to fetch content images:', err);
          alert(`Failed to load images: ${err.message}`);
          setExistingImages([]);
        } finally {
          setIsLoadingImages(false);
        }
      }
    };
    loadImages();
  }, [mode, ideaData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const files = e.files;
    if (files && files.length > 0) {
      setTempFiles(files);
    } else {
      setTempFiles([]);
    }
  };

  const debouncedSetEditorValues = useCallback(debounce((newValues) => setEditorValues(newValues), 300), []);

  const handleEditorChange = (fieldName, value) => {
    setEditorValues((prev) => {
      const newValues = { ...prev, [fieldName]: value };
      debouncedSetEditorValues(newValues);
      return newValues;
    });
  };

  const handleImageUpload = (fieldName, file, resizedUrl) => {
    setFormData((prev) => ({
      ...prev,
      [`${fieldName}_file`]: file,
      [`${fieldName}_img`]: resizedUrl,
      [`${fieldName}_img_original`]: '',
    }));
  };

  const createImageDataList = (uploadedImages, homeId) => {
    const imageDataList = [];
    const currentDate = new Date();
    uploadedImages.forEach((image) => {
      imageDataList.push({
        ImgHome_Img: image.resizedUrl,
        ImgHome_ImgOriginal: image.originalUrl,
        Home_ID: homeId,
        ImgHome_Sort: existingImages.length + imageDataList.length + 1,
        StatusImgHome_ID: 1,
        ImgHome_Date: currentDate.toISOString().split('T')[0],
        ImgHome_Tine: currentDate.toTimeString().split(' ')[0],
        ImgSource_Domain: new URL(image.resizedUrl).hostname,
      });
    });
    return imageDataList;
  };

  const handleDeleteImage = async (imgHomeID) => {
    try {
      await deleteImageContentBlog([imgHomeID]);
      setExistingImages((prevImages) => prevImages.filter((image) => image.ImgHome_ID !== imgHomeID));
      setFormData((prev) => {
        const updatedFormData = { ...prev };
        if (prev.Home_Short_img === existingImages.find((img) => img.ImgHome_ID === imgHomeID)?.ImgHome_Img) {
          updatedFormData.Home_Short_img = existingImages.length > 1 ? existingImages[0]?.ImgHome_Img : '';
          updatedFormData.Home_Short_img_original = existingImages.length > 1 ? existingImages[0]?.ImgHome_ImgOriginal : '';
        }
        if (prev.Home_Detail_img === existingImages.find((img) => img.ImgHome_ID === imgHomeID)?.ImgHome_Img) {
          updatedFormData.Home_Detail_img = existingImages.length > 2 ? existingImages[1]?.ImgHome_Img : '';
          updatedFormData.Home_Detail_img_original = existingImages.length > 2 ? existingImages[1]?.ImgHome_ImgOriginal : '';
        }
        return updatedFormData;
      });
    } catch (error) {
      console.error('Failed to delete image:', error);
      alert(`Failed to delete image: ${error.message}`);
    }
  };

  const handleOrderChange = async (e) => {
    const reorderedImages = e.value;
    setExistingImages(reorderedImages);
    const updatedSortData = reorderedImages.map((image, index) => ({
      ImgHome_ID: image.ImgHome_ID,
      ImgHome_Sort: index + 1,
    }));
    try {
      await updateImageContentBlog(updatedSortData);
      console.log('Image sort order updated successfully');
    } catch (error) {
      console.error('Failed to update image sort order:', error);
      alert(`Failed to update image sort order: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsUploading(true);
      await new Promise((resolve) => setTimeout(resolve, 350));
      let updatedFormData = {
        ...formData,
        Home_Short: editorValues.Home_Short || formData.Home_Short,
        Home_Detail: editorValues.Home_Detail || formData.Home_Detail,
      };

      if (!updatedFormData.Home_Date) updatedFormData.Home_Date = defaultDate;
      if (!updatedFormData.Home_Time) updatedFormData.Home_Time = defaultTime;

      if (mode === 'add') {
        const { Home_Short_img, Home_Short_img_original, Home_Detail_img, Home_Detail_img_original, ...dataToInsert } = updatedFormData;
        const response = await insertContentBlog(dataToInsert);
        if (!response.latest_id || response.latest_id.length === 0) {
          throw new Error('Failed to retrieve latest_id from insert response');
        }
        const latestId = response.latest_id[0];

        if (tempFiles.length > 0) {
          const uploadedImages = await uploadImageContentBlog(tempFiles);
          const imageDataList = createImageDataList(uploadedImages, latestId);
          await insertImageContentBlog(imageDataList);
          const updatedImages = await fetchImageContentBlog(latestId);
          setExistingImages(updatedImages);
          if (imageDataList.length > 0) {
            updatedFormData.Home_Short_img = imageDataList[0].ImgHome_Img;
            updatedFormData.Home_Short_img_original = imageDataList[0].ImgHome_ImgOriginal;
          }
          if (imageDataList.length > 1) {
            updatedFormData.Home_Detail_img = imageDataList[1].ImgHome_Img;
            updatedFormData.Home_Detail_img_original = imageDataList[1].ImgHome_ImgOriginal;
          }
        }
        updatedFormData.Home_ID = latestId;
        onSave(updatedFormData);
      } else {
        const { Home_Short_img, Home_Short_img_original, Home_Detail_img, Home_Detail_img_original, ...dataToUpdate } = updatedFormData;
        await updateContentBlog(dataToUpdate);
        if (tempFiles.length > 0) {
          const uploadedImages = await uploadImageContentBlog(tempFiles);
          const imageDataList = createImageDataList(uploadedImages, formData.Home_ID);
          await insertImageContentBlog(imageDataList);
          const updatedImages = await fetchImageContentBlog(formData.Home_ID);
          setExistingImages(updatedImages);
          if (imageDataList.length > 0) {
            updatedFormData.Home_Short_img = updatedImages[0]?.ImgHome_Img || '';
            updatedFormData.Home_Short_img_original = updatedImages[0]?.ImgHome_ImgOriginal || '';
          }
          if (imageDataList.length > 1) {
            updatedFormData.Home_Detail_img = updatedImages[1]?.ImgHome_Img || '';
            updatedFormData.Home_Detail_img_original = updatedImages[1]?.ImgHome_ImgOriginal || '';
          }
        }
        onSave(updatedFormData);
      }
    } catch (error) {
      console.error('Error saving idea:', error);
      alert(`Failed to ${mode === 'add' ? 'add' : 'update'} idea: ${error.message}`);
    } finally {
      setIsUploading(false);
      setTempFiles([]);
    }
  };

  return (
    <div className="content-ideaslab-form container">
      <form onSubmit={handleSubmit}>
        <div className="d-grid gap-2 d-md-flex justify-content-md-end mb-3">
          <button type="button" onClick={onCancel} className="btn btn-light">
            <FontAwesomeIcon icon={faTimes} /> Cancel
          </button>
          <button type="submit" className="btn btn-dark" disabled={isUploading || isLoadingImages}>
            <FontAwesomeIcon icon={faFloppyDisk} /> {mode === 'add' ? 'Add' : 'Update'} Idea
          </button>
        </div>

        {mode === 'edit' && (
          <div className="mb-3">
            <label className="form-label">Idea ID: {formData.Home_ID}</label>
          </div>
        )}

        <div className="mb-3">
          <label htmlFor="Home_Category" className="form-label">Category:</label>
          <select
            id="Home_Category"
            name="Home_Category"
            className="form-control"
            value={formData.Home_Category}
            onChange={handleChange}
            disabled={isLoadingCategories}
          >
            <option value="">Select a category</option>
            {isLoadingCategories ? (
              <option value="">Loading...</option>
            ) : categoryError ? (
              <option value="">Failed to load categories</option>
            ) : (
              categories.map((category) => (
                <option key={category.ServiceTypes_ID} value={category.ServiceTypes_ID}>
                  {category.ServiceTypes_Name}
                </option>
              ))
            )}
          </select>
          {categoryError && <div className="text-danger mt-1">{categoryError}</div>}
        </div>

        <div className="mb-3">
          <label htmlFor="Home_Sub" className="form-label">Subject:</label>
          <input
            type="text"
            id="Home_Sub"
            name="Home_Sub"
            className="form-control"
            value={formData.Home_Sub}
            onChange={handleChange}
            required
          />
        </div>

        <ImagesUploadSection
          mode={mode}
          isUploading={isUploading}
          isLoadingImages={isLoadingImages}
          existingImages={existingImages}
          tempFiles={tempFiles}
          handleFileSelect={handleFileSelect}
          handleDeleteImage={handleDeleteImage}
          handleOrderChange={handleOrderChange}
          setExistingImages={setExistingImages}
          setFormData={setFormData}
        />

        <EditorField
          fieldName="Home_Short"
          label="Short Description:"
          placeholder="Enter short description..."
          onChange={handleEditorChange}
          onImageUpload={handleImageUpload}
          isUploading={isUploading}
          setIsUploading={setIsUploading}
          initialValue={formData.Home_Short}
        />

        <EditorField
          fieldName="Home_Detail"
          label="Detail:"
          placeholder="Enter detailed description..."
          onChange={handleEditorChange}
          onImageUpload={handleImageUpload}
          isUploading={isUploading}
          setIsUploading={setIsUploading}
          initialValue={formData.Home_Detail}
        />
      </form>
    </div>
  );
};

export default BlogForm;